import type { MinerPalette } from '@pxc/shared';

/**
 * Per-miner shape overrides (docs/ART-DIRECTION.md §6).
 *
 * Every miner shares one rig; rarity changes the palette and two or three
 * shapes, never the skeleton. The distinguishing feature is always the
 * head-and-tool silhouette, so each character is identifiable as a black shape
 * at 64px.
 */

export type HelmetStyle = 'dome' | 'visor' | 'flat' | 'goggles' | 'guard' | 'crest';
export type ToolStyle = 'pickaxe' | 'sledge' | 'mallet' | 'drill' | 'lantern';

export interface MinerArt {
  helmet: HelmetStyle;
  tool: ToolStyle;
  backpack: boolean;
  shoulderPads: boolean;
  coat: boolean;
  /** Seconds per swing — heavier tools are slower. */
  swingSeconds: number;
}

const DEFAULT_ART: MinerArt = {
  helmet: 'dome',
  tool: 'pickaxe',
  backpack: false,
  shoulderPads: false,
  coat: false,
  swingSeconds: 1.1,
};

const BY_ID: Record<string, Partial<MinerArt>> = {
  grit: { helmet: 'dome', tool: 'pickaxe', swingSeconds: 1.1 },
  ember: { helmet: 'visor', tool: 'pickaxe', swingSeconds: 0.95 },
  slate: { helmet: 'flat', tool: 'sledge', shoulderPads: true, swingSeconds: 1.45 },
  quartz: { helmet: 'goggles', tool: 'mallet', swingSeconds: 0.8 },
  onyx: { helmet: 'guard', tool: 'drill', backpack: true, swingSeconds: 0.42 },
  aurum: { helmet: 'crest', tool: 'lantern', coat: true, swingSeconds: 1.6 },
  // The two newest hands. Vector fallback only until their sheets land.
  prism: { helmet: 'goggles', tool: 'pickaxe', shoulderPads: true, swingSeconds: 0.9 },
  garnet: { helmet: 'guard', tool: 'sledge', shoulderPads: true, swingSeconds: 1.5 },
};

export function artFor(minerId: string): MinerArt {
  return { ...DEFAULT_ART, ...(BY_ID[minerId] ?? {}) };
}

export const FALLBACK_PALETTE: MinerPalette = {
  suit: '#3A4756',
  suitDark: '#242E3A',
  accent: '#D4A24C',
  metal: '#8C97A6',
  skin: '#C89B78',
  lampTint: '#FFD79A',
};

/**
 * Where each miner stands, as a percentage of the 1000×1400 scene.
 */
export interface MinerSlot {
  /**
   * Mirror the character horizontally.
   *
   * Every sheet is drawn facing right, so a miner placed to the RIGHT of his
   * crystal cluster swings into empty rock with the ore behind his back. The
   * ones on the left half of each deck are flipped to face their vein.
   */
  flip?: boolean;
  /** Horizontal centre, % of scene width. */
  x: number;
  /** Feet line, % of scene height. */
  y: number;
  scale: number;
}

/**
 * Eight standing places on the backdrop's four painted decks.
 *
 * Each miner stands NEXT TO the crystal cluster painted on his side of the
 * deck, faces it, and is the same colour as it. The backdrop's palette was
 * repainted to match the crew: gold and amber on the floor, white and blue on
 * the third deck, violet and gold on the second, white-gold and red on top. A
 * violet miner chipping at gold ore was the tell that the two were designed
 * apart.
 *
 * Every x is set the same way: four to five per cent of the scene width from
 * the inner edge of its cluster, measured off the artwork rather than judged by
 * eye. Aurum was the outlier at eight per cent, and one miner standing further
 * from his ore than the rest is the kind of gap that reads as sloppiness even
 * when nobody can say why. The left-hand slots are flipped: standing right of the
 * ore while swinging right means picking at bare rock with the vein behind
 * you, which is what made the crew look like it was mining nothing.
 *
 * Slots are ordered bottom-up, so a crew smaller than eight fills the decks
 * nearest the camera first and any spare place is left on the top deck, where
 * an empty spot reads as more shaft rather than as a hole in the layout.
 *
 * Every slot shares one scale. The backdrop is a flat side view, so all decks
 * are the same distance from the camera and faking depth would contradict it.
 */
export const MINER_SLOTS: readonly MinerSlot[] = [
  // Bottom floor: gold left (Grit), amber right (Ember).
  { x: 30, y: 91.8, scale: 1.15, flip: true },
  { x: 69, y: 91.8, scale: 1.15 },
  // Third deck: white left (Slate), blue right (Quartz).
  { x: 22, y: 70.4, scale: 1.15, flip: true },
  { x: 64, y: 70.4, scale: 1.15 },
  // Second deck: violet left (Onyx), gold right (Aurum).
  { x: 35, y: 47.1, scale: 1.15, flip: true },
  { x: 73, y: 47.1, scale: 1.15 },
  // Top deck: white-and-gold left (Prism), red right (Garnet).
  { x: 26, y: 22.9, scale: 1.15, flip: true },
  { x: 60, y: 22.9, scale: 1.15 },
];

/** Rig height in scene units, used to place the HTML hotspot above the head. */
export const RIG_HEIGHT = 168;
export const SCENE_WIDTH = 1000;
/**
 * The backdrop is 1905 units tall; the scene is 1965.
 *
 * The artwork was widened at the sides so its aspect (0.525) is just past a
 * phone's (0.522). That is what lets the mine fill the screen edge to edge
 * without cropping a deck: fitting it inside left dark bands down both sides,
 * and filling the width with the original narrow art cut the top and bottom
 * decks off. The extra 60 units below are room for the crew button.
 */
export const BACKDROP_HEIGHT = 1905;
export const BACKDROP_TOP = 0;
export const SCENE_HEIGHT = 1965;

export function slotFor(index: number): MinerSlot {
  return MINER_SLOTS[index % MINER_SLOTS.length] ?? MINER_SLOTS[0]!;
}
